import { type BaseOptionsType } from 'advanced-filters/data';
import {
  fetchErrorSelector, pendingFilterSelector, subConditionVisibleSelector,
} from 'advanced-filters/selectors/filter';
import { resetPendingFilter, setFetchError } from 'advanced-filters/store/filter';
import { Button, Card, Result } from 'antd';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import BaseTimeFilter from './BaseTimeFilter';
import ConditionComparatorSelect from './FilterInputs/ConditionComparatorSelect';
import ConditionKeySelect from './FilterInputs/ConditionKeySelect';
import ConditionValueSelect from './FilterInputs/ConditionValueSelect';
import SubConditionComparatorSelect from './FilterInputs/SubConditionComparatorSelect';
import SubConditionKeySelect from './FilterInputs/SubConditionKeySelect';
import SubConditionValueSelect from './FilterInputs/SubConditionValueSelect';
import styles from './SearchCriteria.module.scss';
import SearchCriteriaFooter from './SearchCriteriaFooter';

function ErrorState({
  handleClick,
  title,
}: {
  handleClick: () => void;
  title: string;
}) {
  return (
    <Result
      status="error"
      title={title}
      subTitle="Please try Again, if the issue persist, try refreshing the page."
      extra={[<Button onClick={handleClick}>Retry</Button>]}
    />
  );
}

export default function SearchCriteria({
  conditionKeys,
}: {
  conditionKeys: BaseOptionsType[]
}) {
  const dispatch = useDispatch();
  const {
    conditionKey: selectedOption, conditionValue,
    subConditionKey, subConditionComparator,
  } = useSelector(pendingFilterSelector);
  const fetchError = useSelector(fetchErrorSelector);

  const isSubConditionVisible = useSelector(subConditionVisibleSelector);

  const handleResetPendingFilter = useCallback(() => {
    dispatch(resetPendingFilter());
    dispatch(setFetchError(null));
  }, [dispatch]);

  return (
    <Card
      className={styles.container}
      title="Search Criteria"
      classNames={{
        body: styles.cardBody,
      }}
    >
      <div className={styles.cardContent}>
        {
          fetchError ? (
            <ErrorState
              handleClick={handleResetPendingFilter}
              title={fetchError}
            />
          ) : (
            <div className={styles.form}>
              <ConditionKeySelect conditionKeys={conditionKeys} />
              <ConditionComparatorSelect />
              <ConditionValueSelect />
              {isSubConditionVisible && (
                <>
                  <SubConditionKeySelect />
                  {subConditionKey && <SubConditionComparatorSelect />}
                  {subConditionComparator && <SubConditionValueSelect />}
                </>
              )}
              {selectedOption?.hasDateFilter && conditionValue && <BaseTimeFilter />}
            </div>
          )
        }
        <SearchCriteriaFooter />
      </div>
    </Card>
  );
}
